import { httpsCallable } from 'firebase/functions';
import { collection, query, where, doc, updateDoc, orderBy } from 'firebase/firestore';
import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Table } from 'web/components/elements';
import useFirestore from 'web/components/FirebaseContext/useFirestore';
import useAuth from 'web/components/FirebaseContext/useAuth';
import useFunctions from 'web/components/FirebaseContext/useFunctions';
import ScreenTracker from 'web/components/ScreenTracker';
import Spinner from 'web/components/Spinner';
import UserContext from 'web/components/UserContext';
import WithHeaderContentColumn from 'web/components/WithHeaderContentColumn';
import useErrorHandler from 'web/hooks/useErrorHandler';
import useFirestoreCollectionData from 'web/hooks/useFirestoreCollectionData';
import useErrorReporter from 'web/hooks/useErrorReporter';
import themeClasses from 'web/styles/themeClasses.css';
import { firestoreUserConverter } from 'web/utils/convert';
import { signInWithCustomToken } from 'firebase/auth';
import UserCreateForm, { FormValues as UserFormValues } from 'web/components/UserCreateForm';
import GenericReactModal from 'web/components/GenericReactModal';
import Flare from 'web/components/Flare';
import { useNavigate } from 'react-router-dom';

const TeamMembersList = ({ teamId, userId: userId }: { teamId: string; userId: string }) => {
  const firestore = useFirestore();
  const teamQuery = teamId
    ? query(
        collection(firestore, 'users'),
        where('teamMember', '==', teamId),
        orderBy('createdAt', 'asc'),
      ).withConverter(firestoreUserConverter)
    : null;
  const [teamMembers, loading, error] = useFirestoreCollectionData(teamQuery);
  useErrorHandler(error);

  if (loading) return <Spinner />;

  return (
    <Table>
      <thead>
        <tr>
          <th>First Name</th>
          <th>Last Name</th>
          <th>Email</th>
          <th>Booking Page</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {error && (
          <tr>
            <td colSpan={6} style={{ textAlign: 'center', color: '#c00' }}>
              Error loading team members.
            </td>
          </tr>
        )}
        {!error && teamMembers && teamMembers.length === 0 && (
          <tr>
            <td colSpan={6} style={{ textAlign: 'center', color: '#aaa' }}>
              No team members yet.
            </td>
          </tr>
        )}
        {!error &&
          teamMembers &&
          teamMembers.length > 0 &&
          teamMembers.map((member) => (
            <tr key={member.id} style={member.disabled ? { color: '#aaa', background: '#f8f8f8' } : {}}>
              <td>{member.firstName}</td>
              <td>{member.lastName}</td>
              <td>{member.email}</td>
              <td>
                {member.bookingPageSlug ? (
                  <a href={`/${member.bookingPageSlug}`} target="_blank" rel="noopener noreferrer">
                    {member.bookingPageSlug}
                  </a>
                ) : (
                  <span style={{ color: '#aaa' }}>—</span>
                )}
              </td>
              <td>
                {member.disabled ? <Flare variant="warning">Disabled</Flare> : <Flare variant="success">Enabled</Flare>}
              </td>
              <td>
                {member.id !== userId && (
                  <>
                    <TeamLoginAsButton memberId={member.id} disabled={!!member.disabled} />{' '}
                    <TeamEnableDisableButton memberId={member.id} disabled={!!member.disabled} />
                  </>
                )}
              </td>
            </tr>
          ))}
      </tbody>
    </Table>
  );
};

const TeamLoginAsButton: React.FC<{ memberId: string; disabled: boolean }> = ({ memberId, disabled }) => {
  const functions = useFunctions();
  const auth = useAuth();
  const errorReporter = useErrorReporter();
  const [loggingIn, setLoggingIn] = React.useState(false);
  const navigate = useNavigate();

  return (
    <Button
      size="xs"
      variant="secondary"
      onClick={async () => {
        if (loggingIn) return;
        const confirm = window.confirm(
          `You're about to login as another user.\n\n` +
            `This will logout your current user on all tabs.\n` +
            "It's recommended to use a different browser or private mode for that.",
        );
        if (!confirm) return;
        setLoggingIn(true);
        try {
          const res = await httpsCallable<{ userId: string }, { token: string }>(
            functions,
            'teamsLoginSubstituteUser',
          )({ userId: memberId });
          const { token } = res.data;
          await signInWithCustomToken(auth, token);
          navigate('/login');
        } catch (err) {
          errorReporter.report(new Error(`Failed to login as team member: ${err}`));
          window.alert(`Failed to login as team member: ${err}`);
        }
        setLoggingIn(false);
      }}
      disabled={loggingIn || disabled}
    >
      {loggingIn && <Spinner />}
      <span>Log in as</span>
    </Button>
  );
};

const TeamEnableDisableButton: React.FC<{ memberId: string; disabled: boolean }> = ({ memberId, disabled }) => {
  const firestore = useFirestore();
  const errorReporter = useErrorReporter();
  const [submitting, setSubmitting] = React.useState(false);
  return (
    <Button
      size="xs"
      variant={'secondary'}
      disabled={submitting}
      onClick={async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
          await updateDoc(doc(firestore, 'users', memberId), { disabled: !disabled });
        } catch (err) {
          errorReporter.report(new Error(`Failed to ${disabled ? 'enable' : 'disable'} user: ${err}`));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {disabled ? 'Enable' : 'Disable'}
    </Button>
  );
};

const CreateUserButton = () => {
  const functions = useFunctions();
  const errorReporter = useErrorReporter();
  const [creatingUser, setCreatingUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateUser = async (values: UserFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await httpsCallable(
        functions,
        'teamsCreateUser',
      )({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      });
      setCreatingUser(false);
    } catch (err) {
      errorReporter.report(new Error(`Failed to create team member: ${err}`));
      setError('Failed to create user. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setCreatingUser(true);
        }}
      >
        Add
      </Button>
      <GenericReactModal
        isOpen={creatingUser}
        onRequestClose={() => setCreatingUser(false)}
        contentLabel="Create team member"
      >
        <h4 className={themeClasses({ marginY: 0 })}>Add a team member</h4>
        <UserCreateForm
          onSubmit={handleCreateUser}
          submitting={submitting}
          error={error}
          withCountry={false}
          buttonSize="md"
        />
      </GenericReactModal>
    </>
  );
};

const DashboardTeamIndex = () => {
  const { userData } = useContext(UserContext);

  return (
    <>
      <Helmet title="Team" />
      <ScreenTracker screenName="DashboardTeam" />
      <WithHeaderContentColumn
        header="Team members"
        whiteBackground
        extendedHeader={
          <div className={themeClasses({ display: 'flex', gap: 4, alignItems: 'center' })}>
            <CreateUserButton />
          </div>
        }
      >
        {!userData?.teamOwner ? (
          <p>You are not a team owner. This page is only available to team owners.</p>
        ) : (
          <TeamMembersList teamId={userData.teamOwner} userId={userData.id} />
        )}
      </WithHeaderContentColumn>
    </>
  );
};

export default DashboardTeamIndex;
